/**
 * 🚀 VOICE ACTOR ZONE SELECTORS
 * 
 * Zone-based selectors for VoiceActor worksheet data
 * Adapted from BookWorksheet zone selectors for VoiceActor entity hierarchy
 * 
 * Entity Mapping:
 * BookWorksheet → VoiceActor
 * litGroupId   → actorId
 * bookId       → portfolioId
 * characterId  → characterId (same)
 * variationId  → variationId (same)
 */

// ===== ACTOR ZONE SELECTORS =====
export const selectActorZoneData = (state) => state.user.voiceActorWorksheetData?.actorZone;

export const selectActorZoneActors = (state) => state.user.voiceActorWorksheetData?.actorZone?.actors || [];

export const selectActorZoneSelectedActor = (state) => state.user.voiceActorWorksheetData?.actorZone?.selectedActor;

export const selectActorZoneLoading = (state) => state.user.voiceActorWorksheetData?.actorZone?.loading || false;

export const selectActorZoneError = (state) => state.user.voiceActorWorksheetData?.actorZone?.error;

// ===== PORTFOLIO ZONE SELECTORS =====
export const selectPortfolioZoneData = (state) => state.user.voiceActorWorksheetData?.portfolioZone;

export const selectPortfolioZonePortfolios = (state) => state.user.voiceActorWorksheetData?.portfolioZone?.portfolios || [];

export const selectPortfolioZoneActivePortfolio = (state) => state.user.voiceActorWorksheetData?.portfolioZone?.activePortfolio;

export const selectPortfolioZoneLoading = (state) => state.user.voiceActorWorksheetData?.portfolioZone?.loading || false;

export const selectPortfolioZoneError = (state) => state.user.voiceActorWorksheetData?.portfolioZone?.error;

// ===== CHARACTER ZONE SELECTORS =====
export const selectCharacterZoneData = (state) => state.user.voiceActorWorksheetData?.characterZone;

export const selectCharacterZoneCharacters = (state) => state.user.voiceActorWorksheetData?.characterZone?.characters || [];

export const selectCharacterZoneSelectedCharacter = (state) => state.user.voiceActorWorksheetData?.characterZone?.selectedCharacter;

export const selectCharacterZoneLoading = (state) => state.user.voiceActorWorksheetData?.characterZone?.loading || false;

export const selectCharacterZoneError = (state) => state.user.voiceActorWorksheetData?.characterZone?.error;

// ===== VARIATION ZONE SELECTORS =====
export const selectVariationZoneData = (state) => state.user.voiceActorWorksheetData?.variationZone;

export const selectVariationZoneVariations = (state) => state.user.voiceActorWorksheetData?.variationZone?.variations || [];

export const selectVariationZoneActiveVariation = (state) => state.user.voiceActorWorksheetData?.variationZone?.activeVariation;

export const selectVariationZoneActiveVersion = (state) => state.user.voiceActorWorksheetData?.variationZone?.activeVersion;

export const selectVariationZoneSamples = (state) => state.user.voiceActorWorksheetData?.variationZone?.samples || [];

export const selectVariationZoneEditMode = (state) => state.user.voiceActorWorksheetData?.variationZone?.editMode || 'view';

export const selectVariationZoneLoading = (state) => state.user.voiceActorWorksheetData?.variationZone?.loading || false;

export const selectVariationZoneError = (state) => state.user.voiceActorWorksheetData?.variationZone?.error;

export const selectVariationZoneHasUnsavedChanges = (state) => state.user.voiceActorWorksheetData?.variationZone?.hasUnsavedChanges || false;

export const selectVariationZoneIsSubmitting = (state) => state.user.voiceActorWorksheetData?.variationZone?.isSubmitting || false;

export const selectVariationZoneVariationCounts = (state) => state.user.voiceActorWorksheetData?.variationZone?.variationCounts || {};

// ===== SHARED DATA SELECTORS =====
export const selectSharedDataForComponents = (state) => state.user.voiceActorWorksheetData?.sharedData;

export const selectSharedDataActors = (state) => state.user.voiceActorWorksheetData?.sharedData?.actors || [];

export const selectSharedDataPortfolios = (state) => state.user.voiceActorWorksheetData?.sharedData?.portfolios || [];

export const selectSharedDataActorProfiles = (state) => state.user.voiceActorWorksheetData?.sharedData?.actorProfiles || {};

export const selectSharedDataPortfolioProfiles = (state) => state.user.voiceActorWorksheetData?.sharedData?.portfolioProfiles || {};

export const selectSharedDataSelections = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections;

export const selectSharedDataSelectedActorId = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections?.actorId;

export const selectSharedDataSelectedPortfolioId = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections?.portfolioId;

export const selectSharedDataSelectedCharacterId = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections?.characterId;

export const selectSharedDataSelectedVariationId = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections?.variationId;

export const selectSharedDataLoading = (state) => state.user.voiceActorWorksheetData?.sharedData?.loading;

export const selectSharedDataErrors = (state) => state.user.voiceActorWorksheetData?.sharedData?.errors;

export const selectSharedDataLastUpdated = (state) => state.user.voiceActorWorksheetData?.sharedData?.lastUpdated;

// ===== COMPOSITE SELECTORS =====

/**
 * Get the currently selected actor object from shared data
 */
export const selectCurrentActor = (state) => {
  const actorId = selectSharedDataSelectedActorId(state);
  const actors = selectSharedDataActors(state);
  return actors.find(actor => actor.uuid === actorId) || null;
};

/**
 * Get the currently selected portfolio object from shared data
 */
export const selectCurrentPortfolio = (state) => {
  const portfolioId = selectSharedDataSelectedPortfolioId(state);
  const portfolios = selectSharedDataPortfolios(state);
  return portfolios.find(portfolio => portfolio.uuid === portfolioId) || null;
};

/**
 * Get the currently selected character object from character zone
 */
export const selectCurrentCharacter = (state) => {
  const characterId = selectSharedDataSelectedCharacterId(state);
  const characters = selectCharacterZoneCharacters(state);
  return characters.find(character => character.uuid === characterId) || null;
};

/**
 * Get the currently selected variation object from variation zone
 */
export const selectCurrentVariation = (state) => {
  const variationId = selectSharedDataSelectedVariationId(state);
  const variations = selectVariationZoneVariations(state);
  return variations.find(variation => variation.uuid === variationId) || null;
};

/**
 * Check if all required selections are made for variation operations
 */
export const selectHasCompleteSelections = (state) => {
  const actorId = selectSharedDataSelectedActorId(state);
  const portfolioId = selectSharedDataSelectedPortfolioId(state);
  const characterId = selectSharedDataSelectedCharacterId(state);

  return !!(actorId && portfolioId && characterId);
};

/**
 * Get the actor profile for the currently selected actor
 */
export const selectCurrentActorProfile = (state) => {
  const actorId = selectSharedDataSelectedActorId(state);
  const actorProfiles = selectSharedDataActorProfiles(state);
  return actorId ? actorProfiles[actorId] : null;
};

/**
 * Get the portfolio profile for the currently selected portfolio
 */
export const selectCurrentPortfolioProfile = (state) => {
  const portfolioId = selectSharedDataSelectedPortfolioId(state);
  const portfolioProfiles = selectSharedDataPortfolioProfiles(state);
  return portfolioId ? portfolioProfiles[portfolioId] : null;
};

/**
 * Check if any zone is currently loading
 */
export const selectAnyZoneLoading = (state) => {
  return selectActorZoneLoading(state) ||
    selectPortfolioZoneLoading(state) ||
    selectCharacterZoneLoading(state) ||
    selectVariationZoneLoading(state);
};

/**
 * Get all zone errors as an object
 */
export const selectAllZoneErrors = (state) => {
  return {
    actor: selectActorZoneError(state),
    portfolio: selectPortfolioZoneError(state),
    character: selectCharacterZoneError(state),
    variation: selectVariationZoneError(state)
  };
};

/**
 * Check if there are any zone errors
 */
export const selectHasAnyZoneErrors = (state) => {
  const errors = selectAllZoneErrors(state);
  return Object.values(errors).some(error => error !== null);
};
